import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { Search, Filter } from 'lucide-react';
import { auditService } from '../../services/audit-service';
import type { AuditLogDto, AuditLogQueryDto } from '../../types/admin-api';

const columnHelper = createColumnHelper<AuditLogDto>();

export function AuditLogList() {
  const [search, setSearch] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [entityTypeFilter, setEntityTypeFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 20;

  const query: AuditLogQueryDto = {
    page,
    pageSize,
    action: actionFilter || undefined,
    entityType: entityTypeFilter || undefined,
    startDate: startDate || undefined,
    endDate: endDate || undefined,
  };

  const { data, isLoading, error } = useQuery({
    queryKey: ['audit-logs', query],
    queryFn: () => auditService.getAll(query),
  });

  const columns = [
    columnHelper.accessor('timestamp', {
      header: 'Timestamp',
      cell: (info) => (
        <div className="text-ink-primary">
          {new Date(info.getValue()).toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
          })}
        </div>
      ),
    }),
    columnHelper.accessor('adminUserEmail', {
      header: 'Admin User',
      cell: (info) => (
        <div>
          <div className="font-medium text-ink-primary">{info.getValue() || 'Unknown'}</div>
          {info.row.original.ipAddress && (
            <div className="text-xs text-ink-muted">IP: {info.row.original.ipAddress}</div>
          )}
        </div>
      ),
    }),
    columnHelper.accessor('action', {
      header: 'Action',
      cell: (info) => {
        const action = info.getValue();
        const colorClass = action.includes('Disable') || action.includes('Cancel')
          ? 'bg-brand-warm/20 text-brand-warm'
          : action.includes('Enable')
          ? 'bg-brand-confirm/20 text-brand-confirm'
          : 'bg-brand-primary/20 text-brand-primary-light';

        return (
          <span className={`px-2 py-1 rounded text-xs font-medium ${colorClass}`}>
            {action}
          </span>
        );
      },
    }),
    columnHelper.accessor('entityType', {
      header: 'Entity Type',
      cell: (info) => (
        <span className="px-2 py-1 rounded text-xs font-medium bg-white/10 text-ink-secondary">
          {info.getValue()}
        </span>
      ),
    }),
    columnHelper.accessor('entityId', {
      header: 'Entity ID',
      cell: (info) => (
        <span className="text-xs text-ink-muted font-mono">
          {info.getValue().substring(0, 8)}...
        </span>
      ),
    }),
    columnHelper.accessor('reason', {
      header: 'Reason',
      cell: (info) => (
        <div className="max-w-xs truncate text-ink-secondary">
          {info.getValue() || '-'}
        </div>
      ),
    }),
    columnHelper.display({
      id: 'changes',
      header: 'Changes',
      cell: (info) => {
        const hasOldValues = info.row.original.oldValues;
        const hasNewValues = info.row.original.newValues;

        if (!hasOldValues && !hasNewValues) {
          return <span className="text-ink-subtle">-</span>;
        }

        return (
          <div className="text-xs">
            {hasOldValues && (
              <div className="text-brand-warm">
                Old: {JSON.stringify(JSON.parse(info.row.original.oldValues!)).substring(0, 30)}...
              </div>
            )}
            {hasNewValues && (
              <div className="text-brand-confirm">
                New: {JSON.stringify(JSON.parse(info.row.original.newValues!)).substring(0, 30)}...
              </div>
            )}
          </div>
        );
      },
    }),
  ];

  const table = useReactTable({
    data: data?.data || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const actionTypes = [
    'EnableBusiness',
    'DisableBusiness',
    'EnableTrainer',
    'DisableTrainer',
    'EnableFacility',
    'DisableFacility',
    'EnableProduct',
    'DisableProduct',
    'CancelTraining',
  ];

  const entityTypes = [
    'BusinessProfile',
    'TrainerProfile',
    'Facility',
    'Product',
    'Training',
  ];

  if (error) {
    return (
      <div className="bg-brand-warm/20 border border-brand-warm/40 text-brand-warm px-4 py-3 rounded">
        Error loading audit logs: {error instanceof Error ? error.message : 'Unknown error'}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header & Filters */}
      <div className="bg-surface-app-elevated border border-white/10 rounded-xl shadow-sm p-6">
        <div className="flex items-center gap-3 mb-4">
          <Filter size={24} className="text-ink-secondary" />
          <h2 className="font-display text-xl font-semibold tracking-tight text-ink-primary">Audit Logs</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Search by Admin Email */}
          <div>
            <label className="block text-sm font-medium text-ink-secondary mb-1">
              Admin User
            </label>
            <div className="relative">
              <Search
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-ink-subtle"
                size={18}
              />
              <input
                type="text"
                placeholder="Search by email..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                className="w-full pl-10 pr-4 py-2 bg-surface-input border border-white/15 text-ink-primary placeholder-ink-muted rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent"
              />
            </div>
          </div>

          {/* Action Filter */}
          <div>
            <label className="block text-sm font-medium text-ink-secondary mb-1">
              Action Type
            </label>
            <select
              value={actionFilter}
              onChange={(e) => {
                setActionFilter(e.target.value);
                setPage(1);
              }}
              className="w-full px-4 py-2 bg-surface-input border border-white/15 text-ink-primary rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent"
            >
              <option value="">All Actions</option>
              {actionTypes.map((action) => (
                <option key={action} value={action}>
                  {action}
                </option>
              ))}
            </select>
          </div>

          {/* Entity Type Filter */}
          <div>
            <label className="block text-sm font-medium text-ink-secondary mb-1">
              Entity Type
            </label>
            <select
              value={entityTypeFilter}
              onChange={(e) => {
                setEntityTypeFilter(e.target.value);
                setPage(1);
              }}
              className="w-full px-4 py-2 bg-surface-input border border-white/15 text-ink-primary rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent"
            >
              <option value="">All Entities</option>
              {entityTypes.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>

          {/* Start Date */}
          <div>
            <label className="block text-sm font-medium text-ink-secondary mb-1">
              Start Date
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                setPage(1);
              }}
              className="w-full px-4 py-2 bg-surface-input border border-white/15 text-ink-primary rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent"
            />
          </div>

          {/* End Date */}
          <div>
            <label className="block text-sm font-medium text-ink-secondary mb-1">
              End Date
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => {
                setEndDate(e.target.value);
                setPage(1);
              }}
              className="w-full px-4 py-2 bg-surface-input border border-white/15 text-ink-primary rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent"
            />
          </div>

          {/* Clear Filters */}
          {(search || actionFilter || entityTypeFilter || startDate || endDate) && (
            <div className="flex items-end">
              <button
                onClick={() => {
                  setSearch('');
                  setActionFilter('');
                  setEntityTypeFilter('');
                  setStartDate('');
                  setEndDate('');
                  setPage(1);
                }}
                className="w-full px-4 py-2 text-white bg-white/10 border border-white/30 rounded-lg hover:bg-white/20"
              >
                Clear All Filters
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="bg-surface-app-elevated border border-white/10 rounded-xl shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-ink-muted">Loading audit logs...</div>
        ) : data?.data && data.data.length > 0 ? (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-white/10">
                <thead className="bg-white/5">
                  {table.getHeaderGroups().map((headerGroup) => (
                    <tr key={headerGroup.id}>
                      {headerGroup.headers.map((header) => (
                        <th
                          key={header.id}
                          className="px-6 py-3 text-left text-xs font-medium text-ink-secondary uppercase tracking-wider"
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody className="divide-y divide-white/10">
                  {table.getRowModel().rows.map((row) => (
                    <tr key={row.id} className="hover:bg-white/5">
                      {row.getVisibleCells().map((cell) => (
                        <td key={cell.id} className="px-6 py-4 text-sm">
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {data.pagination && data.pagination.totalPages > 1 && (
              <div className="px-6 py-4 border-t border-white/10 flex items-center justify-between">
                <div className="text-sm text-ink-secondary">
                  Showing page {data.pagination.page} of {data.pagination.totalPages} (
                  {data.pagination.totalItems} total)
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage(page - 1)}
                    disabled={page === 1}
                    className="px-4 py-2 bg-white/10 border border-white/30 text-white rounded-lg hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPage(page + 1)}
                    disabled={page >= data.pagination.totalPages}
                    className="px-4 py-2 bg-white/10 border border-white/30 text-white rounded-lg hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="p-8 text-center text-ink-muted">
            No audit logs found matching your filters
          </div>
        )}
      </div>
    </div>
  );
}
